const _ = require("lodash");
const PS = require("./util/ParserState");
const Helpers = require("./util/ParserHelpers");
const Frac = require("../util/Fractions");
const DiagramColors = require("../util/ColorUtil").getDiagramColors();
const findArguments = Helpers.findArguments;
const findOptionalArgument = Helpers.findOptionalArgument;
const symbolInfo = require("./util/RenderHelpers").symbolInfo;

const parseNumberline = function(ps, rangePs, makeNode) {
	ps = PS.convert(ps);
	rangePs = PS.convert(rangePs);
	const range = rangePs.text
		.split(";")
		.map(x => Frac.makeImproper(Frac.parseNumber(x)));
	if (
		range.length !== 2 ||
		!Frac.isValidImproperFraction(range[0]) ||
		!Frac.isValidImproperFraction(range[1])
	) {
		ps.pushError(
			"The numberline range must be two numbers separated by semicolons like {0;1} or {1/5;10/5}."
		);
		return false;
	}
	const numberlineInfo = parseNumberlineBody(ps, range, makeNode);

	if (numberlineInfo) {
		return [
			{
				type: "numberline",
				children: [],
				numberlineInfo: numberlineInfo,
			},
		];
	} else {
		return [
			{
				type: "error",
				name: "numberline",
			},
		];
	}
};

let cachedPattern;
const getCommandPattern = function() {
	if (cachedPattern) {
		return cachedPattern;
	}
	// Get numberline commands in decreasing order of string length so that the
	// parser works even if some command names are prefixes of others.
	let numberlineCommands = Object.keys(numberlineCommandHandlers);
	numberlineCommands = _.sortBy(numberlineCommands, x => -x.length);
	cachedPattern = new RegExp("^\\s*\\\\(" + numberlineCommands.join("|") + ")");
	return cachedPattern;
};

const parseNumberlineBody = function(ps, range, makeNode) {
	const pattern = getCommandPattern();
	const numberlineState = {
		size: "medium",
		endArrow: "none",
		fixed: false, //For engine use only, e.g. kangarooster
		range: range,
		marks: [],
		dots: [],
		shading: [],
		labels: [],
		arrows: [],
		bigArrows: [],
		measurelines: [],
		ticks: [],
		xs: [], //For engine use only, e.g. kangarooster
		checks: [], //For engine use only, e.g. kangarooster
	};

	while (true) {
		const result = pattern.exec(ps.getRest());
		if (!result) {
			break;
		}
		ps.pos += result[0].length;
		const commandName = result[1];
		const handlerResult = numberlineCommandHandlers[commandName](
			ps,
			numberlineState,
			makeNode
		);
		if (!handlerResult) {
			//One of the handlers found an error while parsing.
			return false;
		}
	}
	numberlineState.labels = _.sortBy(numberlineState.labels, label =>
		Frac.makeSortByFunc(label.position)
	);
	return numberlineState;
};

const numberlineCommandHandlers = {
	size: function(ps, state) {
		let lineSize = findArguments(ps, 1);
		if (!lineSize) {
			ps.pushError("The numberline size command requires one argument.");
			return false;
		}
		lineSize = lineSize[0].text;
		lineSize = lineSize.trim().toLowerCase();
		if (["small", "medium", "large", "largepadding"].indexOf(lineSize) >= 0) {
			// largepadding used in target game numberline engines
			state.size = lineSize;
			return true;
		} else {
			ps.pushError(
				"The numberline size command can only have the values small, medium, large, or largepadding."
			);
			return false;
		}
	},
	fixed: function(ps, state) {
		state.fixed = true;
		return true;
	},
	endarrow: function(ps, state) {
		let endArrowInfo = findArguments(ps, 1);
		if (!endArrowInfo) {
			ps.pushError("The numberline endarrow command requires one argument.");
			return false;
		}
		endArrowInfo = endArrowInfo[0].text;
		endArrowInfo = endArrowInfo.trim().toLowerCase();
		if (["left", "right", "both", "none"].indexOf(endArrowInfo) >= 0) {
			state.endArrow = endArrowInfo;
			return true;
		} else {
			ps.pushError(
				"The numberline endarrow command can only have the values left, right, both, or none."
			);
			return false;
		}
	},
	marks: function(ps, state) {
		const markInfo = {size: "medium", side: "both", bold: false};
		if (!parseMarkOptionalArguments(ps, markInfo)) {
			return false;
		}

		let markMultiple = findArguments(ps, 1);
		if (!markMultiple) {
			ps.pushError(
				"numberline marks command requires an argument which contains the value that multiples of will be marked."
			);
			return false;
		}
		markMultiple = Frac.makeImproper(Frac.parseNumber(markMultiple[0].text));
		if (!Frac.isValidImproperFraction(markMultiple)) {
			ps.pushError(
				"numberline marks command argument must be a number like \\marks{3/5} or \\marks{2}."
			);
			return false;
		}
		forEachMultipleInRange(markMultiple, state.range, function(markPosition) {
			if (
				!_.find(
					state.marks,
					mark =>
						Frac.areEquivalent(markPosition, mark.position) &&
						(mark.markInfo.side === "both" ||
							markInfo.side === "both" ||
							mark.markInfo.side === markInfo.side)
				)
			) {
				state.marks.push({
					position: markPosition,
					markInfo: markInfo,
				});
			}
		});
		return true;
	},

	mark: function(ps, state) {
		const markInfo = {size: "medium", side: "both", bold: false, color: null};
		if (!parseMarkOptionalArguments(ps, markInfo)) {
			return false;
		}
		let markPosition = findArguments(ps, 1);
		if (!markPosition) {
			ps.pushError(
				"numberline mark command requires an argument which contains the value that will be marked."
			);
			return false;
		}
		markPosition = Frac.makeImproper(Frac.parseNumber(markPosition[0].text));
		if (!Frac.isValidImproperFraction(markPosition)) {
			ps.pushError(
				"numberline mark command argument must be a number like \\mark{3/5} or \\mark{200}."
			);
			return false;
		}
		if (
			!_.find(
				state.marks,
				mark =>
					Frac.areEquivalent(markPosition, mark.position) &&
					(mark.markInfo.side === "both" ||
						markInfo.side === "both" ||
						mark.markInfo.side === markInfo.side)
			)
		) {
			state.marks.push({
				position: markPosition,
				markInfo: markInfo,
			});
		}
		return true;
	},
	dot: function(ps, state, wrapInfo = {}) {
		// wrapInfo parameters:
		// halfSize = true: makes dot half the size
		const dotInfo = {};
		let optArg = findOptionalArgument(ps);
		while (optArg) {
			const optArgText = optArg.text.trim().toLowerCase();
			if (optArgText.substring(0, 6) === "color=") {
				dotInfo.color = Helpers.parseColor(ps, optArgText);
			} else if (optArgText.substring(0, 5) === "size=") {
				const dotSize = parseFloat(optArgText.substring(5));
				if (!_.isFinite(dotSize) || dotSize <= 0) {
					ps.pushError(
						"numberline dot optional size command must be a positive number."
					);
					return false;
				}
				dotInfo.size = dotSize;
			} else {
				ps.pushError(
					"numberline dot optional command must begin with color= or size="
				);
				return false;
			}
			optArg = findOptionalArgument(ps);
		}
		let dotPosition = findArguments(ps, 1);
		if (!dotPosition) {
			ps.pushError(
				"numberline dot command requires an argument which contains the position to place a dot."
			);
			return false;
		}
		dotPosition = Frac.makeImproper(Frac.parseNumber(dotPosition[0].text));
		if (!Frac.isValidImproperFraction(dotPosition)) {
			ps.pushError(
				"numberline dot command argument must be a number like \\dot{3/5} or \\dot{2}."
			);
			return false;
		}
		dotInfo.position = dotPosition;
		if (!dotInfo.size) {
			dotInfo.size = 1;
		}
		if (wrapInfo.halfSize) {
			dotInfo.size = dotInfo.size / 2;
		}
		state.dots.push(dotInfo);
		return true;
	},
	smalldot: function(ps, state) {
		return this.dot(ps, state, {halfSize: true});
	},
	shade: function(ps, state) {
		const shadeInfo = {side: "both", color: null};
		let shadeArg = findOptionalArgument(ps);
		while (shadeArg) {
			shadeArg = shadeArg.text.trim().toLowerCase();
			if (shadeArg === "top" || shadeArg === "bottom") {
				shadeInfo.side = shadeArg;
			} else if (shadeArg.substring(0, 6) === "color=") {
				shadeInfo.color = Helpers.parseColor(ps, shadeArg);
			} else {
				ps.pushError(
					"numberline shade command can only take in an optional argument of color, top, or bottom."
				);
				return false;
			}
			shadeArg = findOptionalArgument(ps);
		}
		let shadePositions = findArguments(ps, 2);
		if (!shadePositions) {
			ps.pushError(
				"numberline shade command requires two arguments: one for the starting position of the shading and another for the ending position."
			);
			return false;
		}
		shadePositions = shadePositions.map(sp =>
			Frac.makeImproper(Frac.parseNumber(sp.text))
		);
		if (
			!Frac.isValidImproperFraction(shadePositions[0]) ||
			!Frac.isValidImproperFraction(shadePositions[1])
		) {
			ps.pushError(
				"numberline shade command arguments must be numbers like \\shade{1/5}{3/5} or \\shade{-1}{2}."
			);
			return false;
		}
		shadeInfo.positions = shadePositions;
		state.shading.push(shadeInfo);
		return true;
	},
	label: function(ps, state, makeNode) {
		const labelInfo = {
			side: "bottom",
			bold: false,
			size: "normal",
			arrow: "",
			color: "",
			arrowHead: false,
			close: false,
		};
		parseLabelOptionalArguments(ps, labelInfo);
		const labelPosition = findArguments(ps, 1);
		return makeLabelNodeFromArguments(
			ps,
			state,
			labelPosition,
			labelInfo,
			makeNode
		);
	},
	textlabel: function(ps, state, makeNode) {
		const labelInfo = {
			side: "bottom",
			bold: false,
			size: "normal",
			arrow: "",
			color: "",
			arrowHead: false,
			close: false,
			stack: false,
		};
		parseLabelOptionalArguments(ps, labelInfo);
		const labelProps = findArguments(ps, 2);
		return makeLabelNodeFromArguments(
			ps,
			state,
			labelProps,
			labelInfo,
			makeNode
		);
	},
	measureline: function(ps, state, makeNode) {
		const measureLineInfo = {side: "top", color: DiagramColors.defaultblack};
		let range = false;
		let equation = false;
		let measureLineArg = findOptionalArgument(ps);
		while (measureLineArg) {
			measureLineArg = measureLineArg.text.trim().toLowerCase();
			if (measureLineArg === "top") {
				measureLineInfo.side = "top";
			} else if (measureLineArg === "bottom") {
				measureLineInfo.side = "bottom";
			} else if (measureLineArg.substring(0, 6) === "color=") {
				measureLineInfo.color = Helpers.parseColor(ps, measureLineArg);
			} else if (measureLineArg === "range") {
				range = true;
			} else if (measureLineArg === "equation") {
				equation = true;
			} else {
				ps.pushError(
					"numberline measureline command takes only top, bottom, color, range, and equation as optional arguments."
				);
				return false;
			}
			measureLineArg = findOptionalArgument(ps);
		}
		if (range && equation) {
			ps.pushError(
				"numberline measureline command cannot take both range and equation as arguments"
			);
			return false;
		}
		const positions = findArguments(ps, 1)[0]
			.text.split(";")
			.map(x => Frac.makeImproper(Frac.parseNumber(x)));
		const values = [];
		const convertToValue = num =>
			Frac.makeBatex(Frac.makeMixed(Frac.simplifyFraction(num)), {
				noDollars: true,
			});
		for (let i = 0; i < positions.length - 1; i++) {
			const highlow =
				convertToValue(positions[i + 1]) + "-" + convertToValue(positions[i]);
			const difference = convertToValue(
				Frac.subtractImproperFractions(positions[i + 1], positions[i])
			);
			if (range) {
				values.push(makeNode(ps.withText(difference)));
			} else if (equation) {
				values.push(makeNode(ps.withText(highlow + "=" + difference)));
			} else {
				values.push(makeNode(ps.withText(highlow)));
			}
		}
		state.measurelines.push({measureLineInfo, positions, values});
		return true;
	},
	modlabel: function(ps, state, makeNode) {
		const labelInfo = {
			side: "bottom",
			bold: false,
			numberType: "improper",
			size: "normal",
			arrow: "",
			arrowHead: false,
			close: false,
		};
		parseLabelOptionalArguments(ps, labelInfo);
		const labelPositionInfo = findArguments(ps, 3);
		if (!labelPositionInfo) {
			ps.pushError(
				"numberline modlabel command requires three arguments which contains start, end, and skip values of the labels."
			);
			return false;
		}
		let modStart, modEnd;
		if (labelPositionInfo[0].text) {
			modStart = Frac.makeImproper(Frac.parseNumber(labelPositionInfo[0].text));
		} else {
			modStart = _.cloneDeep(state.range[0]);
		}
		if (labelPositionInfo[1].text) {
			modEnd = Frac.makeImproper(Frac.parseNumber(labelPositionInfo[1].text));
		} else {
			modEnd = _.cloneDeep(state.range[1]);
		}
		const skip = Frac.makeImproper(Frac.parseNumber(labelPositionInfo[2].text));
		if (
			!Frac.isValidImproperFraction(modStart) ||
			!Frac.isValidImproperFraction(modEnd) ||
			!Frac.isValidImproperFraction(skip)
		) {
			ps.pushError(
				"numberline modlabel command requires arguments must all be numbers like \\modlabel{0}{1}{1/3}"
			);
			return false;
		}
		const desiredDenominator = skip.denominator;
		forEachMultipleInRange(skip, [modStart, modEnd], function(labelPosition) {
			if (
				!_.find(
					state.labels,
					label =>
						Frac.areEquivalent(labelPosition, label.position) &&
						label.labelInfo.side === labelInfo.side
				)
			) {
				let labelValue = Frac.makeFractionHaveDenominator(
					labelPosition,
					desiredDenominator
				);

				// Otherwise whole numbers are rendered as 5/1, 6/1, 7/1, ...
				if (desiredDenominator === 1) {
					labelValue = Frac.makeMixed(labelValue);
				}

				state.labels.push(
					makeLabel(ps, makeNode, labelValue, labelPosition, labelInfo)
				);
			}
		});
		return true;
	},
	arrow: function(ps, state, makeNode) {
		const arrowInfo = {side: "top", size: "medium", farOut: "low"};
		let arrowArg = findOptionalArgument(ps);
		while (arrowArg) {
			arrowArg =
				arrowArg.text
					.trim()
					.substring(0, 6)
					.toLowerCase() + arrowArg.text.trim().substring(6);
			if (arrowArg === "top" || arrowArg === "bottom") {
				arrowInfo.side = arrowArg;
			} else if (
				arrowArg === "large" ||
				arrowArg === "medium" ||
				arrowArg === "small" ||
				arrowArg === "xlarge" ||
				arrowArg === "huge"
			) {
				arrowInfo.size = arrowArg;
			} else if (arrowArg.substring(0, 6) === "label=") {
				arrowInfo.label = makeNode(
					ps.withText(Helpers.withCommas(arrowArg.substring(6)))
				);
			} else if (arrowArg === "abovelabel") {
				arrowInfo.farOut = "high";
			} else if (arrowArg === "aboveline") {
				arrowInfo.farOut = "mid";
			} else if (arrowArg === "atline") {
				arrowInfo.farOut = "low";
			} else {
				ps.pushError(
					"numberline arrow command can only take in an optional argument of a label, abovelabel, aboveline, atline, top, bottom, small, medium, large, xlarge, or huge."
				);
				return false;
			}
			arrowArg = findOptionalArgument(ps);
		}
		let arrowPositions = findArguments(ps, 2);
		if (!arrowPositions) {
			ps.pushError(
				"numberline arrow command requires two arguments: one for the starting position of the arrow and another for the ending position."
			);
			return false;
		}
		arrowPositions = arrowPositions.map(sp =>
			Frac.makeImproper(Frac.parseNumber(sp.text))
		);
		if (
			!Frac.isValidImproperFraction(arrowPositions[0]) ||
			!Frac.isValidImproperFraction(arrowPositions[1])
		) {
			ps.pushError(
				"numberline arrow command arguments must be numbers like \\arrow{1/5}{3/5} or \\arrow{-1}{2}."
			);
			return false;
		}
		arrowInfo.positions = arrowPositions;
		state.arrows.push(arrowInfo);
		return true;
	},
	tickMarks: function(ps, state, makeNode) {
		const optArg1 = findOptionalArgument(ps);
		const optArg2 = findOptionalArgument(ps);
		const numberlineArguments = findArguments(ps, 3);
		if (!numberlineArguments) {
			ps.pushError(
				"numberline tick marks command requires three arguments: Position of the ticks, number of ticks, and the symbol to use. The symbol can be any symbol allowed by the symbol command."
			);
			return false;
		}
		let numberArgs = [numberlineArguments[0], numberlineArguments[1]];
		const symbolArg = numberlineArguments[2];
		let scale = 1;
		let color = "black";
		if (optArg1 && optArg2) {
			color = optArg1.text ?? "black";
			scale = parseFloat(optArg2.text) ?? 1.0;
		} else if (optArg1) {
			if (isNaN(parseFloat(optArg1.text))) {
				color = optArg1.text ?? "black";
				scale = 1.0;
			} else {
				scale = parseFloat(optArg1.text);
				color = "black";
			}
		}
		numberArgs = numberArgs.map(arg =>
			Frac.makeImproper(Frac.parseNumber(arg.text))
		);
		if (
			!Frac.isValidImproperFraction(numberArgs[0]) ||
			!Frac.isValidImproperFraction(numberArgs[1])
		) {
			ps.pushError(
				"numberline tick marks command arguments must be numbers like \\tickMarks{1/5}{3/5} or \\tickMarks{-1}{2}."
			);
			return false;
		}

		if (numberArgs[1].denominator !== 1) {
			ps.pushError(
				"Number of ticks must be a whole number. If fractional tick marks are desired, reach out to engineering and we can work on that too."
			);
			return false;
		}

		const mappedKeys = _.filter(
			_.map(symbolInfo, symbol => symbol.mappedKey),
			key => key !== null
		);
		let symbolName = "x";
		if (
			!symbolInfo[symbolArg.text] &&
			!_.includes(mappedKeys, symbolArg.text)
		) {
			const symbolNamesWithKeys = [];
			_.each(symbolInfo, function(value, key) {
				symbolNamesWithKeys.push(
					key + (value.mappedKey !== null ? " (" + value.mappedKey + ")" : "")
				);
			});

			ps.pushError(
				symbolArg.text +
					" is not a valid symbol nickname. Possible names are the following: " +
					symbolNamesWithKeys.join(", ") +
					"."
			);
			return false;
		} else {
			symbolName = symbolArg.text;
		}

		const symbolNode = {
			type: "symbol",
			name: symbolName,
			color,
		};
		const ticksInfo = {
			symbolNode,
			number: numberArgs[1],
			position: numberArgs[0],
			scale,
		};
		state.ticks.push(ticksInfo);
		return true;
	},
	arrowSeq: function(ps, state, makeNode) {
		const arrowInfo = {side: "top", size: "medium", farOut: "low"};
		let arrowArg = findOptionalArgument(ps);
		while (arrowArg) {
			if (
				arrowArg.text
					.trim()
					.substring(0, 6)
					.toLowerCase() === "label="
			) {
				arrowInfo.label = makeNode(
					ps.withText(Helpers.withCommas(arrowArg.text.trim().substring(6)))
				);
				arrowArg = findOptionalArgument(ps);
				continue;
			}
			arrowArg = arrowArg.text.trim().toLowerCase();
			if (arrowArg === "top" || arrowArg === "bottom") {
				arrowInfo.side = arrowArg;
			} else if (
				arrowArg === "large" ||
				arrowArg === "medium" ||
				arrowArg === "small" ||
				arrowArg === "xlarge" ||
				arrowArg === "huge"
			) {
				arrowInfo.size = arrowArg;
			} else if (arrowArg === "abovelabel") {
				arrowInfo.farOut = "high";
			} else if (arrowArg === "aboveline") {
				arrowInfo.farOut = "mid";
			} else if (arrowArg === "atline") {
				arrowInfo.farOut = "low";
			} else {
				ps.pushError(
					"numberline arrow command can only take in an optional argument of a label, abovelabel, aboveline, atline, top, bottom, small, medium, large, xlarge, or huge."
				);
				return false;
			}
			arrowArg = findOptionalArgument(ps);
		}

		const args = findArguments(ps, 4);

		const start = parseInt(args[0].text, 10);
		const end = parseInt(args[1].text, 10);
		const skip = parseInt(args[2].text, 10);
		const denominator = parseInt(args[3].text, 10);

		if (!args) {
			ps.pushError(
				"numberline arrow command requires four arguments: start, end, skip, denominator."
			);
			return false;
		}

		const toPos = function(num) {
			return {
				denominator: denominator,
				numerator: num,
				whole: null,
			};
		};

		const numArrows = Math.floor((end - start) / skip);

		_.each(_.range(numArrows), n => {
			const p = n * skip + start;
			state.arrows.push(
				_.extend(
					{
						positions: [toPos(p), toPos(p + skip)],
					},
					arrowInfo
				)
			);
		});

		return true;
	},
	bigarrow: function(ps, state) {
		const arrowInfo = {side: "bottom"};
		let arrowArg = findOptionalArgument(ps);
		if (arrowArg) {
			arrowArg = arrowArg.text.trim().toLowerCase();
			if (arrowArg === "top" || arrowArg === "bottom") {
				arrowInfo.side = arrowArg;
			} else {
				ps.pushError(
					"numberline bigarrow command can only take in an optional argument of top or bottom."
				);
				return false;
			}
		}
		let arrowPosition = findArguments(ps, 1);
		if (!arrowPosition) {
			ps.pushError(
				"numberline bigarrow command requires an argument which contains the position to place the big arrow."
			);
			return false;
		}
		arrowPosition = Frac.makeImproper(Frac.parseNumber(arrowPosition[0].text));
		if (!Frac.isValidImproperFraction(arrowPosition)) {
			ps.pushError(
				"numberline bigarrow command argument must be a number like \\bigarrow{3/5} or \\bigarrow{2}."
			);
			return false;
		}
		arrowInfo.position = arrowPosition;
		state.bigArrows.push(arrowInfo);
		return true;
	},
	xs: function(ps, state) {
		const xInfo = {};
		let xPosition = findArguments(ps, 1);
		if (!xPosition) {
			ps.pushError(
				"numberline xs command requires an argument which contains the position to place the xs."
			);
			return false;
		}
		xPosition = Frac.makeImproper(Frac.parseNumber(xPosition[0].text));
		if (!Frac.isValidImproperFraction(xPosition)) {
			ps.pushError(
				"numberline x command argument must be a number like \\xs{3/5} or \\xs{2}."
			);
			return false;
		}
		xInfo.position = xPosition;
		state.xs.push(xInfo);
		return true;
	},
	checks: function(ps, state) {
		const checkInfo = {};
		let checkPosition = findArguments(ps, 1);
		if (!checkPosition) {
			ps.pushError(
				"numberline checks command requires an argument which contains the position to place the checks."
			);
			return false;
		}
		checkPosition = Frac.makeImproper(Frac.parseNumber(checkPosition[0].text));
		if (!Frac.isValidImproperFraction(checkPosition)) {
			ps.pushError(
				"numberline check command argument must be a number like \\checks{3/5} or \\checks{2}."
			);
			return false;
		}
		checkInfo.position = checkPosition;
		state.checks.push(checkInfo);
		return true;
	},
	blank: function(ps, state, makeNode) {
		return parseBlank(ps, state, makeNode, "blank");
	},
	fracblank: function(ps, state, makeNode) {
		return parseBlank(ps, state, makeNode, "fracblank");
	},
	mixblank: function(ps, state, makeNode) {
		return parseBlank(ps, state, makeNode, "mixblank");
	},
	modblank: function(ps, state, makeNode) {
		return parseModBlank(ps, state, makeNode, "blank");
	},
	modfracblank: function(ps, state, makeNode) {
		return parseModBlank(ps, state, makeNode, "fracblank");
	},
	modmixblank: function(ps, state, makeNode) {
		return parseModBlank(ps, state, makeNode, "mixblank");
	},
};

const parseModBlank = function(ps, state, makeNode, blankName) {
	const blankInfo = {side: "bottom", arrow: "", arrowHead: false, close: false};
	parseBlankOptionalArguments(ps, blankInfo);
	const blankArgs = findArguments(ps, 4);
	if (!blankArgs) {
		ps.pushError(
			"numberline modblank, modfracblank, and modmixblank commands requires four arguments: three for start, end, and skip of the blank positions and one for the size of the blank."
		);
		return false;
	}
	let modStart, modEnd;
	if (blankArgs[0].text) {
		modStart = Frac.makeImproper(Frac.parseNumber(blankArgs[0].text));
	} else {
		modStart = _.cloneDeep(state.range[0]);
	}
	if (blankArgs[1].text) {
		modEnd = Frac.makeImproper(Frac.parseNumber(blankArgs[1].text));
	} else {
		modEnd = _.cloneDeep(state.range[1]);
	}
	const skip = Frac.makeImproper(Frac.parseNumber(blankArgs[2].text));
	if (
		!Frac.isValidImproperFraction(modStart) ||
		!Frac.isValidImproperFraction(modEnd) ||
		!Frac.isValidImproperFraction(skip)
	) {
		ps.pushError(
			"numberline modblank, modfracblank, and modmixblank commands arguments must all be number positions or blank sizes like \\modblank{0}{1}{1/3}{2}"
		);
		return false;
	}
	const blankSize = parseInt(blankArgs[3].text);
	if (!_.isFinite(blankSize) || blankSize <= 0) {
		ps.pushError(
			"numberline modblank, modfracblank, and modmixblank commands' fourth argument must be a positive integer character size like \\modblank{0}{1}{1/3}{2} or \\modfracblank{0}{1}{1/3}{3}."
		);
		return false;
	}
	forEachMultipleInRange(skip, [modStart, modEnd], function(blankPosition) {
		if (
			!_.find(
				state.labels,
				label =>
					Frac.areEquivalent(blankPosition, label.position) &&
					label.labelInfo.side === blankInfo.side
			)
		) {
			state.labels.push(
				makeBlank(ps, makeNode, blankSize, blankPosition, blankInfo, blankName)
			);
		}
	});
	return true;
};

const parseBlank = function(ps, state, makeNode, blankName) {
	const blankInfo = {side: "bottom", arrow: "", arrowHead: false, close: false};
	parseBlankOptionalArguments(ps, blankInfo);
	const blankArgs = findArguments(ps, 2);
	if (!blankArgs) {
		ps.pushError(
			"numberline blank, fracblank, and mixblank commands require two arguments: one for the position that the blank will be placed at and one for the size of the blank."
		);
		return false;
	}
	const blankPosition = Frac.makeImproper(Frac.parseNumber(blankArgs[0].text));
	if (!Frac.isValidImproperFraction(blankPosition)) {
		ps.pushError(
			"numberline blank, fracblank, and mixblank command's first argument must be a number like \\blank{3/5}{2} or \\fracblank{2}{2}."
		);
		return false;
	}
	const blankSize = parseInt(blankArgs[1].text);
	if (!_.isFinite(blankSize) || blankSize <= 0) {
		ps.pushError(
			"numberline blank, fracblank, and mixblank command's second argument must be a positive integer character size like \\blank{3/5}{2} or \\mixblank{2}{2}."
		);
		return false;
	}
	if (
		!_.find(
			state.labels,
			label =>
				Frac.areEquivalent(blankPosition, label.position) &&
				label.labelInfo.side === blankInfo.side
		)
	) {
		state.labels.push(
			makeBlank(ps, makeNode, blankSize, blankPosition, blankInfo, blankName)
		);
	}
	return true;
};

const makeBlank = function(
	ps,
	makeNode,
	blankSize,
	blankPosition,
	blankInfo,
	blankName
) {
	let blankBatex = "\\" + blankName + "{" + blankSize + "}";
	if (blankName === "mixblank") {
		blankBatex += "{" + blankSize + "}";
	}
	const blankPs = ps.withText(blankBatex);
	blankPs.pos = 0;
	const blankNode = makeNode(blankPs);
	return {
		position: blankPosition,
		labelInfo: blankInfo,
		labelNode: blankNode,
	};
};

const makeLabel = function(ps, makeNode, labelValue, labelPosition, labelInfo) {
	let labelBatex;
	if (labelInfo.text) {
		labelBatex = labelInfo.text;
	} else {
		if (labelInfo.numberType === "mixed") {
			labelValue = Frac.makeMixed(labelValue);
		}
		labelBatex = Frac.makeBatex(labelValue, {noDollars: true});
		if (Frac.isWhole(labelValue) && Frac.isNegative(labelValue)) {
			//Add a phantom neg to center the number under the mark
			labelBatex += "\\phantom{\\neg}";
		}
	}
	if (labelInfo.bold) {
		labelBatex = "\\bold{" + labelBatex + "}";
	}
	if (labelInfo.color) {
		labelBatex = "\\color{" + labelInfo.color + "}{" + labelBatex + "}";
	}
	const labelPs = ps.withText(labelBatex);
	labelPs.pos = 0;
	const labelNode = makeNode(labelPs);
	return {
		position: labelPosition,
		labelInfo: labelInfo,
		labelNode: labelNode,
	};
};

const parseBlankOptionalArguments = function(ps, blankInfo) {
	let optArg = findOptionalArgument(ps);
	const optArgs = [];
	while (optArg) {
		optArg = optArg.text.trim().toLowerCase();
		if (
			[
				"top",
				"bottom",
				"bigarrow",
				"smallarrow",
				"tinyarrow",
				"arrowhead",
				"close",
			].indexOf(optArg) < 0
		) {
			ps.pushError(
				optArg +
					" is not a valid optional argument for a blank or modblank. Valid optional arguments are top, bottom, close, bigarrow, smallarrow, and arrowhead."
			);
			return false;
		}
		optArgs.push(optArg);
		optArg = findOptionalArgument(ps);
	}
	if (optArgs.indexOf("top") >= 0) {
		blankInfo.side = "top";
	} else if (optArgs.indexOf("bottom") >= 0) {
		blankInfo.side = "bottom";
	}

	blankInfo.arrow = "";
	if (optArgs.indexOf("bigarrow") >= 0) {
		blankInfo.arrow = "big";
	}
	if (optArgs.indexOf("smallarrow") >= 0) {
		blankInfo.arrow = "small";
	}
	if (optArgs.indexOf("tinyarrow") >= 0) {
		blankInfo.arrow = "tiny";
	}
	if (optArgs.indexOf("close") >= 0) {
		blankInfo.close = true;
	}
	if (
		(optArgs.indexOf("smallarrow") >= 0 && optArgs.indexOf("bigarrow") >= 0) ||
		(optArgs.indexOf("bigarrow") >= 0 && optArgs.indexOf("tinyarrow") >= 0) ||
		(optArgs.indexOf("tinyarrow") >= 0 && optArgs.indexOf("smallarrow") >= 0)
	) {
		ps.pushError(
			"You cannot specify more than one of bigarrow, smallarrow, and tinyarrow."
		);
		return false;
	}
	blankInfo.arrowHead = false;
	if (optArgs.indexOf("arrowhead") >= 0) {
		if (
			!(
				optArgs.indexOf("smallarrow") >= 0 ||
				optArgs.indexOf("bigarrow") >= 0 ||
				optArgs.indexOf("tinyarrow") >= 0
			)
		) {
			ps.pushError(
				"If arrowhead is specified, you must specify bigarrow or smallarrow as optional arguments."
			);
			return false;
		}
	}
};

const parseLabelOptionalArguments = function(ps, labelInfo) {
	let optArg = findOptionalArgument(ps);
	while (optArg) {
		optArg = optArg.text.trim().toLowerCase();

		if ((optArg === "bigarrow" || optArg === "smallarrow") && labelInfo.arrow) {
			if (
				!(
					(labelInfo.arrow === "small" && optArg === "smallarrow") ||
					(labelInfo.arrow === "big" && optArg === "bigarrow") ||
					(labelInfo.arrow === "tiny" && optArg === "tinyarrow")
				)
			) {
				ps.pushError(
					"You cannot specify more than one of bigarrow, smallarrow, and tinyarrow options."
				);
				return false;
			}
		}

		if (optArg === "top") {
			labelInfo.side = "top";
		} else if (optArg === "bottom") {
			labelInfo.side = "bottom";
		} else if (optArg === "bold") {
			labelInfo.bold = true;
		} else if (optArg === "small") {
			labelInfo.size = "small";
		} else if (optArg === "close") {
			labelInfo.close = true;
		} else if (optArg === "bigarrow") {
			labelInfo.arrow = "big";
		} else if (optArg === "smallarrow") {
			labelInfo.arrow = "small";
		} else if (optArg === "tinyarrow") {
			labelInfo.arrow = "tiny";
		} else if (optArg === "arrowhead") {
			labelInfo.arrowHead = true;
		} else if (optArg === "mixed") {
			labelInfo.numberType = "mixed";
		} else if (optArg === "overlap") {
			labelInfo.overlap = true;
		} else if (_.startsWith(optArg, "color=")) {
			labelInfo.color = Helpers.parseColor(ps, optArg);
		} else if (optArg === "stack") {
			// Defaults to top or stacks above label already there
			labelInfo.side = "top";
			labelInfo.stack = true;
		} else {
			ps.pushError(
				optArg +
					" is not a valid optional argument for a label or modlabel. Valid optional arguments are colors, top, bottom, bold, small, close, bigarrow, smallarrow, arrowhead, stack, and mixed (for modlabel only)."
			);
			return false;
		}
		optArg = findOptionalArgument(ps);
	}
	if (labelInfo.arrowHead && !labelInfo.arrow) {
		ps.pushError(
			"If arrowhead is specified, you must specify bigarrow or smallarrow as optional arguments."
		);
		return false;
	}
};

const makeLabelNodeFromArguments = function(
	ps,
	state,
	labelArgs,
	labelInfo,
	makeNode
) {
	if (!labelArgs) {
		ps.pushError("numberline label command is missing argument(s).");
		return false;
	}
	const labelValue = Frac.parseNumber(labelArgs[0].text);
	const labelPosition = Frac.makeImproper(labelValue);
	const labelText = labelArgs[1] && labelArgs[1].text;
	labelInfo.text = labelText;
	if (!Frac.isValidImproperFraction(labelPosition)) {
		ps.pushError(
			"numberline label position argument must be a number like \\label{3/5} or \\label{2}."
		);
		return false;
	}
	const samePositionLabel = _.find(
		state.labels,
		label =>
			Frac.areEquivalent(labelPosition, label.position) &&
			label.labelInfo.side === labelInfo.side &&
			!label.labelInfo.stack
	);
	if (!samePositionLabel || labelInfo.overlap) {
		state.labels.push(
			makeLabel(ps, makeNode, labelValue, labelPosition, labelInfo)
		);
	} else if (samePositionLabel && labelInfo.stack) {
		// If two labels in same position and one is stacked,
		// change stacked label's side option to "stack"
		labelInfo.side = "stack";
		state.labels.push(
			makeLabel(ps, makeNode, labelValue, labelPosition, labelInfo)
		);
	}
	return true;
};

const parseMarkOptionalArguments = function(ps, markInfo) {
	let optArg = findOptionalArgument(ps);
	while (optArg) {
		optArg = optArg.text.trim().toLowerCase();
		if (optArg === "top") {
			markInfo.side = "top";
		} else if (optArg === "bottom") {
			markInfo.side = "bottom";
		} else if (optArg === "bold") {
			markInfo.bold = true;
		} else if (optArg === "small") {
			markInfo.size = "small";
		} else if (optArg === "medium") {
			markInfo.size = "medium";
		} else if (optArg === "large") {
			markInfo.size = "large";
		} else if (optArg.substring(0, 6) === "color=") {
			markInfo.color = Helpers.parseColor(ps, optArg);
		} else {
			ps.pushError(
				optArg +
					" is not a valid optional argument for marks. Valid optional arguments are top, bottom, bold, small, medium, large, and any diagram colors."
			);
			return false;
		}
		optArg = findOptionalArgument(ps);
	}
	return true;
};

const forEachMultipleInRange = function(multiple, range, f) {
	let currFrac = Frac.firstMultipleAbove(range[0], multiple);
	while (Frac.isLessThanOrEqualTo(currFrac, range[1])) {
		f(currFrac);
		currFrac = Frac.plus(currFrac, multiple);
	}
};

module.exports = {
	parseNumberline: parseNumberline,
};
